import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, FileSignature } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * PV de réception management for a single order.
 * Fields: signed (bool), signed_date, has_reserves (bool), reserves_notes, reserves_cleared_date.
 */
export const PvDialog = ({ open, onClose, order, onSaved }) => {
  const [form, setForm] = useState({
    pv_signed: false,
    pv_signed_date: "",
    pv_has_reserves: false,
    pv_reserves_notes: "",
    pv_reserves_cleared_date: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!order) return;
    setForm({
      pv_signed: !!order.pv_signed,
      pv_signed_date: order.pv_signed_date || "",
      pv_has_reserves: !!order.pv_has_reserves,
      pv_reserves_notes: order.pv_reserves_notes || "",
      pv_reserves_cleared_date: order.pv_reserves_cleared_date || "",
    });
  }, [order]);

  const save = async () => {
    try {
      setSaving(true);
      await api.updateOrder(order.id, {
        pv_signed: form.pv_signed,
        pv_signed_date: form.pv_signed ? (form.pv_signed_date || null) : null,
        pv_has_reserves: form.pv_signed ? form.pv_has_reserves : false,
        pv_reserves_notes: form.pv_signed && form.pv_has_reserves ? (form.pv_reserves_notes || null) : null,
        pv_reserves_cleared_date: form.pv_signed && form.pv_has_reserves ? (form.pv_reserves_cleared_date || null) : null,
      });
      toast.success("PV de réception enregistré");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-[520px]" data-testid="pv-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSignature className="h-5 w-5 text-indigo-600" />PV de réception</DialogTitle>
          <DialogDescription>
            {order?.reference} — {order?.client_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center gap-3">
            <Checkbox
              id="pv-signed"
              checked={form.pv_signed}
              onCheckedChange={(v) => setForm({ ...form, pv_signed: !!v })}
              data-testid="pv-signed-checkbox"
            />
            <Label htmlFor="pv-signed" className="cursor-pointer font-medium">
              PV signé par le client
            </Label>
          </div>

          {form.pv_signed && (
            <>
              <div>
                <Label className="text-xs">Date de signature</Label>
                <Input
                  type="date"
                  value={form.pv_signed_date}
                  onChange={(e) => setForm({ ...form, pv_signed_date: e.target.value })}
                  data-testid="pv-signed-date"
                />
              </div>

              <div className="flex items-center gap-3">
                <Checkbox
                  id="pv-has-reserves"
                  checked={form.pv_has_reserves}
                  onCheckedChange={(v) => setForm({ ...form, pv_has_reserves: !!v })}
                  data-testid="pv-has-reserves-checkbox"
                />
                <Label htmlFor="pv-has-reserves" className="cursor-pointer">
                  Avec réserves
                </Label>
              </div>

              {form.pv_has_reserves && (
                <div className="space-y-3 border-l-2 border-amber-400 pl-3">
                  <div>
                    <Label className="text-xs">Réserves à lever</Label>
                    <Textarea
                      rows={3}
                      value={form.pv_reserves_notes}
                      onChange={(e) => setForm({ ...form, pv_reserves_notes: e.target.value })}
                      placeholder="Ex: Retouche peinture salon, joint baignoire à refaire…"
                      data-testid="pv-reserves-notes"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Date de levée des réserves (optionnel)</Label>
                    <Input
                      type="date"
                      value={form.pv_reserves_cleared_date}
                      onChange={(e) => setForm({ ...form, pv_reserves_cleared_date: e.target.value })}
                      data-testid="pv-reserves-cleared-date"
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Annuler</Button>
          <Button onClick={save} disabled={saving} data-testid="pv-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PvDialog;
